import React, { useState } from 'react';
import { Reveal } from '../components/Reveal';
import { Mail, MapPin, Phone, Send, Building2, Users, Newspaper, Check, Heart, Briefcase, Globe } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { createContact } from '../lib/api';
import { useSection } from '../contexts/ContentContext';

const ICON_MAP = { Building2, Users, Newspaper, Heart, Mail, Phone, Briefcase, Globe };

export default function Contact() {
  const contactPage = useSection('contact');
  const settings = useSection('settings');
  const hero = contactPage.hero || {};
  const channels = contactPage.channels || [];
  const visit = contactPage.visitBlock || {};
  const info = settings.contact || {};

  const [form, setForm] = useState({ name: '', email: '', subject: 'general', message: '', city: '' });
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createContact(form);
      toast({ title: 'Message envoyé !', description: 'Nous vous répondrons sous 48h ouvrées.' });
      setSent(true);
      setForm({ name: '', email: '', subject: 'general', message: '', city: '' });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible d\'envoyer le message. Réessayez.', variant: 'destructive' });
    }
  };

  return (
    <>
      <section className="pt-32 pb-12 bg-gradient-to-br from-sky-50 via-white to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            {hero.eyebrow && <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-emerald-600 mb-4">{hero.eyebrow}</span>}
            <h1 className="font-display font-extrabold text-5xl lg:text-7xl text-sky-950 mb-6 leading-tight">
              {hero.title} <span className="gradient-text">{hero.titleHighlight}</span>
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 leading-relaxed">{hero.description}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-6 mb-12">
            {channels.map((c, i) => {
              const Icon = ICON_MAP[c.icon] || Mail;
              return (
                <Reveal key={i} delay={i * 80}>
                  <div className="p-6 rounded-3xl border border-sky-100 bg-gradient-to-br from-white to-sky-50/30 hover:shadow-md transition-all h-full">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center mb-4">
                      <Icon className="w-6 h-6 text-sky-700" />
                    </div>
                    <h3 className="font-display font-bold text-lg text-sky-950 mb-2">{c.title}</h3>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{c.desc}</p>
                    {c.email && <a href={`mailto:${c.email}`} className="text-sm font-semibold text-sky-700 hover:text-sky-900">{c.email}</a>}
                  </div>
                </Reveal>
              );
            })}
          </div>

          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-10">
            <Reveal>
              <div className="bg-white rounded-3xl border border-sky-100 p-8">
                <h2 className="font-display font-extrabold text-2xl text-sky-950 mb-2">Formulaire de contact</h2>
                <p className="text-sm text-slate-500 mb-6">Nous répondons sous 48 heures ouvrées.</p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Nom complet *</Label>
                      <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl border-sky-200 mt-1.5" />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email *</Label>
                      <Input id="email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl border-sky-200 mt-1.5" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Ville</Label>
                      <Input id="city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="h-11 rounded-xl border-sky-200 mt-1.5" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Objet</Label>
                      <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                        <SelectTrigger className="h-11 rounded-xl border-sky-200 mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">Question générale</SelectItem>
                          <SelectItem value="commune">Projet pour ma commune</SelectItem>
                          <SelectItem value="benevolat">Devenir bénévole</SelectItem>
                          <SelectItem value="partenariat">Partenariat</SelectItem>
                          <SelectItem value="presse">Presse / média</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Message *</Label>
                    <Textarea id="message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Présentez brièvement votre demande..." className="rounded-xl border-sky-200 mt-1.5 resize-none" />
                  </div>
                  <Button type="submit" disabled={sent} className="h-12 px-7 bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white font-bold rounded-xl border-0">
                    {sent ? (<><Check className="mr-2 w-4 h-4" />Envoyé !</>) : (<>Envoyer le message<Send className="ml-2 w-4 h-4" /></>)}
                  </Button>
                </form>
              </div>
            </Reveal>

            <Reveal delay={150}>
              <div className="space-y-4">
                <div className="p-6 rounded-3xl bg-gradient-to-br from-sky-950 to-emerald-950 text-white">
                  <h3 className="font-display font-bold text-xl mb-5">Informations pratiques</h3>
                  <ul className="space-y-4 text-sm">
                    {info.address && (
                      <li className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold mb-0.5">Adresse</p>
                          <p className="text-sky-200 whitespace-pre-line">{info.address}</p>
                        </div>
                      </li>
                    )}
                    {info.email && (
                      <li className="flex items-start gap-3">
                        <Mail className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold mb-0.5">Email général</p>
                          <a href={`mailto:${info.email}`} className="text-sky-200 hover:text-emerald-300">{info.email}</a>
                        </div>
                      </li>
                    )}
                    {info.phone && (
                      <li className="flex items-start gap-3">
                        <Phone className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold mb-0.5">Téléphone</p>
                          <a href={`tel:${(info.phone || '').replace(/\s/g, '')}`} className="text-sky-200 hover:text-emerald-300">{info.phone}</a>
                          {info.hours && <p className="text-xs text-sky-300 mt-1">{info.hours}</p>}
                        </div>
                      </li>
                    )}
                  </ul>
                </div>

                {(visit.title || visit.description) && (
                  <div className="p-6 rounded-3xl border border-sky-100 bg-gradient-to-br from-white to-emerald-50/40">
                    <h4 className="font-display font-bold text-sky-950 mb-3">{visit.title}</h4>
                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{visit.description}</p>
                    {visit.ctaEmail && <a href={`mailto:${visit.ctaEmail}`} className="text-sm font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1">{visit.ctaLabel || 'Contact'} <Send className="w-3 h-3" /></a>}
                  </div>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
